(() => {
  const cleanupRoots = new Map();
  const initializedElements = new Set();
  const globalCleanups = [];
  const dialogCloseTimers = new WeakMap();
  const productBlockedStates = new Map();
  let dialogOpener = null;
  let globalInitialized = false;
  let activeInteraction = 'idle';
  let closeHeaderSearch = () => {};

  const setInteractionState = (state, opener = null) => {
    activeInteraction = state;
    document.documentElement.dataset.headerInteraction = state;
    document.body.dataset.headerInteraction = state;
    document.querySelectorAll('[data-header]').forEach((header) => {
      header.dataset.headerInteraction = state;
    });
    if (opener) dialogOpener = opener;
  };

  const clearInteractionState = (state) => {
    if (state && activeInteraction !== state) return;
    activeInteraction = 'idle';
    delete document.documentElement.dataset.headerInteraction;
    delete document.body.dataset.headerInteraction;
    document.querySelectorAll('[data-header]').forEach((header) => {
      header.dataset.headerInteraction = 'idle';
    });
    document.dispatchEvent(new CustomEvent('zaynab:interaction-close', { detail: { state } }));
  };

  const elements = (scope, selector) => {
    const matches = [];
    if (scope?.matches?.(selector)) matches.push(scope);
    matches.push(...(scope?.querySelectorAll?.(selector) || []));
    return matches;
  };

  const listen = (element, eventName, handler, cleanups, options) => {
    if (!element || typeof element.addEventListener !== 'function') return;
    element.addEventListener(eventName, handler, options);
    cleanups.push(() => element.removeEventListener?.(eventName, handler, options));
  };

  const physicalPieceIdentity = (sku, variantId = '') => {
    const match = /^ZAY-SIM-(S(?:0[1-9]|10))-(KIMONO|PANTS|BUNDLE)(?:-(M|L))?$/.exec(String(sku || '').trim().toUpperCase());
    if (!match) return { set: '', kind: '', keys: variantId ? [`variant:${variantId}`] : [] };
    const [, set, kind, size] = match;
    if (kind === 'KIMONO') return { set, kind, keys: [`${set}:kimono`] };
    if (kind === 'PANTS' && size) return { set, kind, keys: [`${set}:pants:${size}`] };
    if (kind === 'BUNDLE' && size) return { set, kind, keys: [`${set}:kimono`, `${set}:pants:${size}`] };
    return { set, kind, keys: variantId ? [`variant:${variantId}`] : [] };
  };

  const cartStateForVariant = (cart, option) => {
    const variantId = String(option?.value || '');
    const selected = physicalPieceIdentity(option?.dataset?.sku, variantId);
    const items = (cart?.items || []).filter((item) => Number(item.quantity) > 0);
    const exact = items.find((item) => String(item.variant_id) === variantId);
    if (exact) return { kind: 'exact', conflicts: [exact], canUpgrade: false };
    const conflicts = items.filter((item) => {
      const current = physicalPieceIdentity(item.sku, item.variant_id);
      return selected.keys.some((key) => current.keys.includes(key));
    });
    const canUpgrade = selected.kind === 'BUNDLE' && conflicts.length > 0 && conflicts.every((item) => {
      const current = physicalPieceIdentity(item.sku, item.variant_id);
      return current.set === selected.set && (current.kind === 'KIMONO' || current.kind === 'PANTS');
    });
    return { kind: conflicts.length ? 'overlap' : 'available', conflicts, canUpgrade };
  };

  const drawerMotionDuration = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 280;

  const setStatus = (element, message, focus = false) => {
    if (!element) return;
    element.textContent = message || '';
    if (focus) element.focus({ preventScroll: true });
  };

  const storeRoot = () => window.Shopify?.routes?.root || '/';

  const updateCartCount = (count) => {
    const normalizedCount = Number.isFinite(Number(count)) ? Number(count) : 0;
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(normalizedCount);
    });
    document.querySelectorAll('[data-cart-link]').forEach((link) => {
      const template = link.dataset.cartLabelTemplate || 'Bag, __COUNT__ items';
      link.setAttribute('aria-label', template.replace('__COUNT__', String(normalizedCount)));
    });
  };

  const getCart = async () => {
    const response = await fetch(`${storeRoot()}cart.js?_=${Date.now()}`, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Cart refresh failed');
    const cart = await response.json();
    updateCartCount(cart.item_count);
    return cart;
  };

  const focusCareTarget = (target) => {
    if (!target || !target.closest('.z-care-topics, .z-care-policies, .z-care-navigation')) return;
    if (!target.hasAttribute('tabindex') && !target.matches('a[href], button, input, select, textarea, summary')) target.setAttribute('tabindex', '-1');
    window.setTimeout(() => target.focus({ preventScroll: true }), 0);
  };

  const initializeCareLinks = (scope) => {
    elements(scope, '[data-care-link]').forEach((link) => {
      if (initializedElements.has(link)) return;
      const cleanups = [];
      listen(link, 'click', () => {
        let target;
        try { target = link.hash ? document.querySelector(link.hash) : null; } catch (error) { target = null; }
        focusCareTarget(target);
      }, cleanups);
      initializedElements.add(link);
      cleanupRoots.set(link, cleanups);
    });
  };

  const initializeWorldGateways = (scope) => {
    elements(scope, '[data-module-id="world.gateways"]').forEach((nav) => {
      if (initializedElements.has(nav)) return;
      const previous = nav.querySelector('[data-world-gateway-prev]');
      const next = nav.querySelector('[data-world-gateway-next]');
      const controls = nav.querySelector('.z-world-gateways__controls');
      const cards = [...nav.querySelectorAll(':scope > a')];
      if (!previous || !next || cards.length < 2) return;
      const cleanups = [];
      const getStep = () => {
        const card = cards[0];
        const gap = Number.parseFloat(getComputedStyle(nav).columnGap || getComputedStyle(nav).gap) || 0;
        return card.getBoundingClientRect().width + gap;
      };
      const updateControls = () => {
        const maxScroll = Math.max(0, nav.scrollWidth - nav.clientWidth);
        const scrollPosition = nav.scrollLeft;
        const atStart = scrollPosition <= 1;
        const atEnd = scrollPosition >= maxScroll - 1;
        previous.disabled = atStart;
        next.disabled = atEnd;
        previous.hidden = atStart;
        next.hidden = atEnd;
        controls?.style.setProperty('--world-gateway-scroll-offset', `${scrollPosition}px`);
        controls?.classList.toggle('is-at-start', atStart && !atEnd);
        controls?.classList.toggle('is-at-end', atEnd && !atStart);
      };
      const move = (direction) => {
        const distance = getStep() * direction;
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        if (typeof nav.scrollBy === 'function') nav.scrollBy({ left: distance, behavior });
        else nav.scrollLeft += distance;
      };
      listen(previous, 'click', () => move(-1), cleanups);
      listen(next, 'click', () => move(1), cleanups);
      listen(nav, 'scroll', updateControls, cleanups, { passive: true });
      listen(window, 'resize', updateControls, cleanups, { passive: true });
      updateControls();
      initializedElements.add(nav);
      cleanupRoots.set(nav, cleanups);
    });
  };

  const initializeMotion = (scope) => {
    const applyMotionPreference = () => {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      elements(scope, 'video[autoplay], video[data-motion-paused="true"]').forEach((video) => {
        if (reducedMotion) {
          video.pause();
          video.removeAttribute('autoplay');
          video.dataset.motionPaused = 'true';
        } else if (video.dataset.motionPaused === 'true') {
          video.setAttribute('autoplay', '');
          video.play().catch(() => {});
          delete video.dataset.motionPaused;
        }
      });
    };
    applyMotionPreference();
    if (!globalInitialized) {
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      listen(reducedMotion, 'change', () => initializeMotion(document), globalCleanups);
      cleanupRoots.set(document, globalCleanups);
    }
  };

  const initializeHeroMedia = (scope) => {
    elements(scope, '[data-hero-video], [data-collection-hero-video], .home-hero__media > video').forEach((video) => {
      if (initializedElements.has(video)) return;
      const cleanups = [];
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const connection = navigator.connection;
      const constrainedConnection = Boolean(connection?.saveData || /^(slow-)?2g$/.test(connection?.effectiveType || ''));
      let frameId = 0;
      let timerId = 0;
      let retryId = 0;
      let liveAt = 0;
      let recoveryHook = null;

      const telemetry = (event = '', error = null) => {
        const entry = { t: performance.now().toFixed(0), event, visibility: document.visibilityState, paused: video.paused, readyState: video.readyState, networkState: video.networkState };
        if (error) entry.error = error;
        try {
          window.__zayVideo = window.__zayVideo || [];
          if (window.__zayVideo.length > 500) window.__zayVideo.shift();
          window.__zayVideo.push(entry);
        } catch (e) {}
        return entry;
      };
      window.__zayVideoReport = () => {
        const src = (() => {
          try {
            return video.currentSrc || [...video.querySelectorAll('source')].map((source) => source.src).join(', ');
          } catch (e) {
            return String(e);
          }
        })();
        return {
          log: window.__zayVideo || [],
          media: { src, paused: video.paused, readyState: video.readyState, networkState: video.networkState, currentTime: video.currentTime, muted: video.muted, autoplay: video.hasAttribute('autoplay'), heroReady: video.hasAttribute('data-hero-video-ready') }
        };
      };

      const reveal = () => video.setAttribute('data-hero-video-ready', '');
      const markLive = () => { liveAt = performance.now(); };
      listen(video, 'playing', () => { telemetry('playing'); reveal(); markLive(); }, cleanups);
      listen(video, 'timeupdate', markLive, cleanups);
      listen(video, 'pause', () => telemetry('pause'), cleanups);
      listen(video, 'waiting', () => telemetry('waiting'), cleanups);
      listen(video, 'stalled', () => telemetry('stalled'), cleanups);
      listen(video, 'error', () => telemetry('error', { code: video.error?.code, message: video.error?.message }), cleanups);

      const attemptPlay = () => video.play?.().then(() => {
        if (video.readyState >= 2) reveal();
        markLive();
        return true;
      }).catch((error) => {
        telemetry('play-rejected', { name: error?.name, message: error?.message });
        return false;
      });

      const recover = (time) => {
        if (!video.isConnected || reducedMotionQuery.matches || constrainedConnection) return;
        if (time > 0 && video.readyState >= 1) {
          try { video.currentTime = time; } catch (error) {}
        }
        attemptPlay();
      };

      const scheduleRecovery = (time) => {
        if (recoveryHook) video.removeEventListener('canplay', recoveryHook);
        const hook = () => recover(time);
        recoveryHook = hook;
        video.addEventListener('canplay', hook, { once: true });
        retryId = window.setTimeout(() => {
          if (!video.isConnected || reducedMotionQuery.matches || constrainedConnection) return;
          if (!video.paused && performance.now() - liveAt < 1500) return;
          recover(time);
        }, 2500);
      };

      const start = () => {
        if (!video.isConnected || reducedMotionQuery.matches || constrainedConnection) {
          telemetry('start:blocked');
          return;
        }
        if (document.visibilityState !== 'visible') {
          telemetry('start:not-visible');
          return;
        }
        video.preload = 'metadata';
        video.setAttribute('autoplay', '');
        window.clearTimeout(retryId);
        const resumeTime = Number.isFinite(video.currentTime) && video.currentTime > 0 ? video.currentTime : 0;
        const needsReload = video.readyState === 0 || (liveAt > 0 && performance.now() - liveAt > 2500);
        telemetry('start', { stalled: needsReload, resumeTime });
        if (needsReload) {
          video.load();
          telemetry('load:recovering-position', { resumeTime });
          scheduleRecovery(resumeTime);
          return;
        }
        attemptPlay().then((played) => {
          if (!video.isConnected) return;
          const markAtPlay = liveAt;
          window.clearTimeout(retryId);
          retryId = window.setTimeout(() => {
            if (!video.isConnected || reducedMotionQuery.matches || constrainedConnection) return;
            if (document.visibilityState !== 'visible') return;
            if (video.paused || liveAt === markAtPlay) {
              telemetry('retry:stalled');
              const at = Number.isFinite(video.currentTime) && video.currentTime > 0 ? video.currentTime : 0;
              video.load();
              scheduleRecovery(at);
            }
          }, 1200);
          if (played) return;
          video.addEventListener('canplay', () => { if (!video.isConnected) return; attemptPlay(); }, { once: true });
        });
      };

      if (!reducedMotionQuery.matches && !constrainedConnection) {
        frameId = window.requestAnimationFrame(() => {
          timerId = window.setTimeout(start, 250);
        });

        if ('IntersectionObserver' in window) {
          let settleId = 0;
          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                window.clearTimeout(settleId);
                start();
              } else {
                window.clearTimeout(settleId);
                settleId = window.setTimeout(() => video.pause(), 250);
              }
            });
          }, { rootMargin: '400px 0px' });
          observer.observe(video);
          cleanups.push(() => {
            observer.disconnect();
            window.clearTimeout(settleId);
          });
        }

        listen(window, 'pageshow', (event) => {
          if (!event.persisted) return;
          telemetry('pageshow:persisted');
          if (document.visibilityState !== 'visible') return;
          start();
        }, cleanups);

        listen(document, 'visibilitychange', () => {
          if (document.visibilityState === 'visible') start();
        }, cleanups);

        listen(document, 'freeze', () => { telemetry().event = 'freeze'; }, cleanups);
        listen(document, 'resume', () => { telemetry().event = 'resume'; start(); }, cleanups);
      }

      cleanups.push(() => {
        if (frameId) window.cancelAnimationFrame(frameId);
        if (timerId) window.clearTimeout(timerId);
        if (retryId) window.clearTimeout(retryId);
      });
      initializedElements.add(video);
      cleanupRoots.set(video, cleanups);
    });
  };

  const initializeManifestoReveal = (scope) => {
    elements(scope, '[data-reveal="manifesto"]').forEach((section) => {
      if (initializedElements.has(section)) return;
      const cleanups = [];
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      section.dataset.revealEnabled = 'true';

      if (reducedMotionQuery.matches || !('IntersectionObserver' in window)) {
        section.classList.add('is-visible');
      } else {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            section.classList.add('is-visible');
            observer.unobserve(section);
          });
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });
        observer.observe(section);
        cleanups.push(() => observer.disconnect());
      }

      listen(reducedMotionQuery, 'change', (event) => {
        if (event.matches) section.classList.add('is-visible');
      }, cleanups);
      initializedElements.add(section);
      cleanupRoots.set(section, cleanups);
    });
  };

  const initializeSealReveal = (scope) => {
    elements(scope, '[data-reveal="seal"]').forEach((seal) => {
      if (initializedElements.has(seal)) return;
      const cleanups = [];
      const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      seal.dataset.revealEnabled = 'true';

      if (reducedMotionQuery.matches || !('IntersectionObserver' in window)) {
        seal.classList.add('is-visible');
      } else {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            seal.classList.add('is-visible');
            observer.unobserve(seal);
          });
        }, { rootMargin: '0px 0px -12% 0px', threshold: 0.15 });
        observer.observe(seal);
        cleanups.push(() => observer.disconnect());
      }

      listen(reducedMotionQuery, 'change', (event) => {
        if (event.matches) seal.classList.add('is-visible');
      }, cleanups);
      initializedElements.add(seal);
      cleanupRoots.set(seal, cleanups);
    });
  };

  const initializeCollectionFilters = (scope) => {
    elements(scope, '[data-catalog-filter-bar]').forEach((bar) => {
      if (initializedElements.has(bar)) return;
      const cleanups = [];
      const details = [...bar.querySelectorAll('[data-catalog-filter]')];
      const syncDetailState = (detail) => {
        const trigger = detail.querySelector(':scope > summary');
        if (trigger) trigger.setAttribute('aria-expanded', String(detail.open));
        detail.dataset.catalogFilterState = detail.open ? 'open' : 'closed';
      };
      const closeDetails = (restoreFocus = false) => {
        details.forEach((detail) => {
          if (!detail.open) return;
          detail.open = false;
          syncDetailState(detail);
          if (restoreFocus) detail.querySelector(':scope > summary')?.focus({ preventScroll: true });
        });
      };
      const closeOtherDetails = (event) => {
        if (!event.target.open) {
          syncDetailState(event.target);
          return;
        }
        details.forEach((detail) => {
          if (detail !== event.target && detail.open) {
            detail.open = false;
            syncDetailState(detail);
          }
        });
        syncDetailState(event.target);
      };
      details.forEach((detail) => {
        const trigger = detail.querySelector(':scope > summary');
        syncDetailState(detail);
        listen(detail, 'toggle', closeOtherDetails, cleanups);
        listen(trigger, 'pointerdown', () => { detail.dataset.catalogFilterPointer = 'true'; }, cleanups);
        listen(trigger, 'keydown', () => { delete detail.dataset.catalogFilterPointer; }, cleanups);
        listen(detail, 'keydown', (event) => {
          if (event.key !== 'Escape' || !detail.open) return;
          event.preventDefault();
          detail.open = false;
          syncDetailState(detail);
          detail.querySelector(':scope > summary')?.focus({ preventScroll: true });
        }, cleanups);
      });
      listen(document, 'pointerdown', (event) => {
        if (!bar.contains(event.target)) closeDetails();
      }, cleanups);
      listen(bar, 'change', (event) => {
        const input = event.target.closest?.('.z-catalog-filter__panel input');
        const form = input?.form;
        if (!input || !form) return;
        window.setTimeout(() => form.requestSubmit?.(), 0);
      }, cleanups);

      const sizeFilter = bar.querySelector('[data-catalog-size-filter]');
      if (sizeFilter) {
        const catalog = bar.closest('.z-catalog');
        const cards = [...(catalog?.querySelectorAll('.z-catalog-grid .z-product-card') || [])];
        const editorial = [...(catalog?.querySelectorAll('.z-catalog-grid .z-catalog-editorial') || [])];
        const options = [...sizeFilter.querySelectorAll('[data-catalog-size-option]')];
        const readSize = () => {
          const value = new URL(window.location.href).searchParams.get('filter.v.option.size');
          return value ? value.toLowerCase().replace(/\s+/g, '-') : '';
        };
        const applySize = (size) => {
          cards.forEach((card) => {
            const values = String(card.dataset.catalogSizeOptions || '').split(',').filter(Boolean);
            card.hidden = Boolean(size) && !values.includes(size);
          });
          editorial.forEach((figure) => { figure.hidden = Boolean(size); });
          options.forEach((option) => {
            if (option.dataset.sizeValue === size) option.setAttribute('aria-current', 'true');
            else option.removeAttribute('aria-current');
          });
          sizeFilter.dataset.activeSize = size;
        };
        options.forEach((option) => listen(option, 'click', (event) => {
          event.preventDefault();
          const url = new URL(option.href, window.location.origin);
          if (option.dataset.sizeValue) url.searchParams.set('filter.v.option.size', option.dataset.sizeValue === 'one-size' ? 'One size' : option.dataset.sizeValue.toUpperCase());
          else url.searchParams.delete('filter.v.option.size');
          window.history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
          applySize(option.dataset.sizeValue || '');
        }, cleanups));
        listen(window, 'popstate', () => applySize(readSize()), cleanups);
        applySize(readSize());
      }

      initializedElements.add(bar);
      cleanupRoots.set(bar, cleanups);
    });
  };

  const clearDialogCloseTimer = (dialog) => {
    const timer = dialogCloseTimers.get(dialog);
    if (!timer) return;
    window.clearTimeout(timer);
    dialogCloseTimers.delete(dialog);
  };

  const closeDialog = (dialog, { immediate = false } = {}) => {
    if (!dialog?.open) return;
    clearDialogCloseTimer(dialog);
    const isBag = dialog.id === 'BagDialog';
    const duration = isBag && !immediate ? drawerMotionDuration() : 0;
    if (isBag && duration > 0) {
      if (dialog.dataset.dialogState === 'closing') return;
      dialog.dataset.dialogState = 'closing';
      const timer = window.setTimeout(() => {
        dialogCloseTimers.delete(dialog);
        if (dialog.open) dialog.close();
      }, duration);
      dialogCloseTimers.set(dialog, timer);
      return;
    }
    dialog.close();
  };

  const refreshBagDialog = async (dialog, { focus = true } = {}) => {
    if (!dialog) return false;
    const response = await fetch(`${storeRoot()}cart?_=${Date.now()}`, { headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Bag refresh failed');
    const freshDialog = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('#BagDialog');
    const freshSurface = freshDialog?.querySelector('.z-dialog__surface');
    const currentSurface = dialog.querySelector('.z-dialog__surface');
    if (!freshSurface || !currentSurface) return false;
    updateCartCount(freshDialog.dataset.cartItemCount);
    dialog.dataset.cartItemCount = freshDialog.dataset.cartItemCount;
    cleanupScope(currentSurface);
    currentSurface.replaceWith(freshSurface);
    initializeCartForms(dialog);
    resyncProductBlockedStates();
    if (focus && dialog.open) {
      const initialFocus = freshSurface.querySelector('[data-dialog-initial-focus]') || freshSurface.querySelector('input, button, select, textarea, a[href]');
      window.requestAnimationFrame(() => { if (dialog.open) initialFocus?.focus({ preventScroll: true }); });
    }
    return true;
  };

  const openDialog = (dialog, opener = null) => {
    if (!dialog?.showModal) return false;
    const mobileMenu = opener?.closest?.('[data-mobile-menu]');
    mobileMenu?.close();
    closeHeaderSearch();
    document.querySelectorAll('[data-dialog][open]').forEach((openDialog) => closeDialog(openDialog, { immediate: true }));
    dialogOpener = mobileMenu ? document.querySelector('[data-mobile-menu-open]') : opener;
    clearDialogCloseTimer(dialog);
    dialog.showModal();
    if (dialog.id === 'BagDialog') dialog.dataset.dialogState = 'opening';
    setInteractionState(dialog.id === 'BagDialog' ? 'bag' : 'dialog', dialogOpener);
    const initialFocus = dialog.querySelector('[data-dialog-initial-focus]') || dialog.querySelector('input, button, select, textarea, a[href]');
    window.requestAnimationFrame(() => {
      if (dialog.id === 'BagDialog' && dialog.open && dialog.dataset.dialogState === 'opening') dialog.dataset.dialogState = 'open';
      initialFocus?.focus();
    });
    return true;
  };

  const initializeCollectionVideo = (scope) => {
    elements(scope, '[data-collection-video]').forEach((root) => {
      if (initializedElements.has(root)) return;
      const video = root.querySelector('video');
      const playButton = root.querySelector('[data-collection-video-play]');
      const fallback = root.querySelector('[data-collection-video-fallback]');
      if (!video || !playButton) return;
      const cleanups = [];
      video.removeAttribute('autoplay');
      video.controls = false;
      if (fallback) fallback.hidden = Boolean(video.poster);
      const revealControls = () => {
        video.controls = true;
        playButton.hidden = true;
        root.dataset.collectionVideoState = 'started';
      };
      const play = () => {
        const playPromise = video.play?.();
        playPromise?.catch?.(() => {
          playButton.hidden = false;
          root.dataset.collectionVideoState = 'ready';
        });
      };
      listen(playButton, 'click', play, cleanups);
      listen(video, 'play', revealControls, cleanups);
      listen(video, 'error', () => {
        playButton.hidden = true;
        if (fallback) fallback.hidden = false;
        root.dataset.collectionVideoState = 'unavailable';
      }, cleanups);
      initializedElements.add(root);
      cleanupRoots.set(root, cleanups);
    });
  };

  const initializeProductGallery = (root) => {
    const gallery = root.querySelector('[data-product-gallery]');
    if (!gallery || initializedElements.has(gallery)) return;
    const items = [...gallery.querySelectorAll('[data-gallery-item]')];
    const dots = [...root.querySelectorAll('[data-gallery-dot]')];
    if (items.length < 2 || dots.length === 0) return;
    const cleanups = [];
    let frameId = 0;
    const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const setActive = (index, move = false) => {
      const normalizedIndex = Math.max(0, Math.min(index, items.length - 1));
      dots.forEach((dot, dotIndex) => {
        if (dotIndex === normalizedIndex) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (!move) return;
      const item = items[normalizedIndex];
      item?.scrollIntoView?.({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'start' });
      item?.focus?.({ preventScroll: true });
    };
    const readActive = () => {
      frameId = 0;
      if (!gallery.clientWidth) return;
      const center = gallery.scrollLeft + gallery.clientWidth / 2;
      let activeIndex = 0;
      let distance = Number.POSITIVE_INFINITY;
      items.forEach((item, index) => {
        const itemCenter = item.offsetLeft + item.offsetWidth / 2;
        const nextDistance = Math.abs(itemCenter - center);
        if (nextDistance < distance) { distance = nextDistance; activeIndex = index; }
      });
      setActive(activeIndex);
    };
    const onScroll = () => {
      if (!frameId) frameId = window.requestAnimationFrame(readActive);
    };
    listen(gallery, 'scroll', onScroll, cleanups, { passive: true });
    items.forEach((item, index) => listen(item, 'keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      setActive(index + (event.key === 'ArrowRight' ? 1 : -1), true);
    }, cleanups));
    dots.forEach((dot, index) => listen(dot, 'click', () => setActive(index, true), cleanups));
    setActive(0);
    initializedElements.add(gallery);
    cleanupRoots.set(gallery, [...cleanups, () => { if (frameId) window.cancelAnimationFrame(frameId); }]);
  };

  const initializeDialogs = (scope) => {
    elements(scope, '[data-dialog-open]').forEach((opener) => {
      if (initializedElements.has(opener)) return;
      const cleanups = [];
      const handler = (event) => {
        const dialog = document.getElementById(opener.dataset.dialogOpen);
        if (!dialog?.showModal) return;
        event.preventDefault();
        openDialog(dialog, opener);
        if (dialog.id === 'BagDialog') refreshBagDialog(dialog).catch(() => {});
      };
      listen(opener, 'click', handler, cleanups);
      initializedElements.add(opener);
      cleanupRoots.set(opener, cleanups);
    });

    elements(scope, '[data-dialog]').forEach((dialog) => {
      if (initializedElements.has(dialog)) return;
      const cleanups = [];
      listen(dialog, 'click', (event) => { if (event.target === dialog || event.target.closest?.('[data-dialog-close]')) closeDialog(dialog); }, cleanups);
      listen(dialog, 'cancel', (event) => { event.preventDefault(); closeDialog(dialog); }, cleanups);
      listen(dialog, 'close', () => {
        clearDialogCloseTimer(dialog);
        delete dialog.dataset.dialogState;
        if (dialogOpener) { dialogOpener.focus(); dialogOpener = null; }
        clearInteractionState(dialog.id === 'BagDialog' ? 'bag' : 'dialog');
      }, cleanups);
      cleanups.push(() => clearDialogCloseTimer(dialog));
      initializedElements.add(dialog);
      cleanupRoots.set(dialog, cleanups);
    });
  };

  const hiddenSearchResource = (item) => {
    const title = String(item?.title || '').trim().toLowerCase();
    const handle = String(item?.handle || '').trim().toLowerCase();
    const url = String(item?.url || '').trim().toLowerCase();
    const hiddenHandles = ['archive', 'accessories', 'home', 'watermelon', 'journal', 'bags', 'frontpage'];
    const hiddenCollectionUrl = /\/collections\/(archive|accessories|home|watermelon|bags|frontpage)(?:\/|$)/.test(url);
    const hiddenPageUrl = /\/pages\/journal(?:\/|$)/.test(url);
    return hiddenHandles.includes(title) || hiddenHandles.includes(handle)
      || hiddenCollectionUrl || hiddenPageUrl || url.includes('/blogs/');
  };

  window.ZaynabTheme = window.ZaynabTheme || {};
  window.ZaynabTheme.hiddenSearchResource = hiddenSearchResource;

  const initializePredictiveSearch = (scope) => {
    elements(scope, '[data-predictive-search]').forEach((root) => {
      if (initializedElements.has(root)) return;
      const cleanups = [];
      const form = root.closest('form');
      const input = form?.querySelector('input[name="q"]') || root.parentElement?.querySelector('input[name="q"]');
      const results = root.querySelector('[data-predictive-results]');
      const status = root.querySelector('[data-predictive-status]');
      let requestController;
      let requestTimer;
      const clearResults = () => { results?.replaceChildren(); setStatus(status, ''); };
      const addGroup = (label, items) => {
        if (!results || !items?.length) return;
        const group = document.createElement('section');
        const heading = document.createElement('h3');
        heading.textContent = label;
        group.append(heading);
        const list = document.createElement('ul');
        items.slice(0, 4).forEach((item) => {
          const entry = document.createElement('li');
          const link = document.createElement('a');
          link.href = new URL(item.url, window.location.origin).href;
          link.textContent = item.title || item.handle || label;
          entry.append(link);
          list.append(entry);
        });
        group.append(list);
        results.append(group);
      };
      const search = async () => {
        const query = input?.value.trim() || '';
        if (query.length < 2) { clearResults(); return; }
        requestController?.abort();
        requestController = new AbortController();
        setStatus(status, 'Searching');
        const url = new URL(`${storeRoot()}search/suggest.json`, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product,collection,article,page');
        url.searchParams.set('resources[limit]', '4');
        try {
          const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: requestController.signal });
          if (!response.ok) throw new Error('Predictive search failed');
          const data = (await response.json()).resources?.results || {};
          results?.replaceChildren();
          addGroup('Products', data.products?.filter((item) => !hiddenSearchResource(item)));
          addGroup('Collections', data.collections?.filter((item) => !hiddenSearchResource(item)));
          addGroup('Pages', data.pages?.filter((item) => !hiddenSearchResource(item)));
          setStatus(status, results?.textContent ? 'Suggestions ready' : 'No suggestions');
        } catch (error) {
          if (error.name === 'AbortError') return;
          clearResults();
          setStatus(status, 'Search suggestions are unavailable');
        }
      };
      listen(input, 'input', () => { window.clearTimeout(requestTimer); requestTimer = window.setTimeout(search, 180); }, cleanups);
      listen(form, 'submit', (event) => { event.preventDefault(); input?.focus(); }, cleanups);
      initializedElements.add(root);
      cleanupRoots.set(root, cleanups);
    });
  };

  const initializeProduct = (root) => {
    if (initializedElements.has(root)) return;
    const form = root.querySelector('[data-product-form]');
    if (!form) return;
    const select = form.querySelector('[data-variant-select]');
    const submit = form.querySelector('[data-add-to-bag]');
    const remakeOpener = form.querySelector('[data-remake-opener]');
    const status = form.querySelector('[data-product-status]');
    const overlapNote = form.querySelector('[data-product-overlap-note]');
    const variants = () => select ? [...select.options] : [...form.querySelectorAll('input[name="id"]')];
    const variantCartStates = new Map();
    const cleanups = [];
    let submitting = false;

    const matchesVariant = (option, values) => values.every((value, index) => !value || option.dataset[`option${index + 1}`] === value);
    const selectedValues = () => [...root.querySelectorAll('[data-option-index]')].map((fieldset) => fieldset.querySelector('[data-option-value][aria-pressed="true"]')?.dataset.optionValue || null);
    const selectedVariant = () => {
      const options = variants();
      const values = selectedValues();
      return options.find((option) => values.some(Boolean) && matchesVariant(option, values)) || select?.selectedOptions[0] || options[0];
    };
    const measurementTabs = [...root.querySelectorAll('[data-measurement-tab]')];
    const setMeasurementKind = (kind, moveFocus = false) => {
      if (!measurementTabs.length) return;
      const activeTab = measurementTabs.find((tab) => tab.dataset.measurementTab === kind && !tab.hidden) || measurementTabs.find((tab) => !tab.hidden);
      if (!activeTab) return;
      measurementTabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.setAttribute('aria-selected', String(isActive));
        tab.classList.toggle('is-active', isActive);
        tab.tabIndex = isActive ? 0 : -1;
        const panel = document.getElementById(tab.getAttribute('aria-controls'));
        if (panel) panel.hidden = !isActive;
      });
      if (moveFocus) activeTab.focus({ preventScroll: true });
    };
    const onMeasurementTabKeydown = (event) => {
      const visibleTabs = measurementTabs.filter((tab) => !tab.hidden);
      const currentIndex = visibleTabs.indexOf(event.currentTarget);
      if (currentIndex === -1) return;
      let nextIndex = currentIndex;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % visibleTabs.length;
      else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = visibleTabs.length - 1;
      else return;
      event.preventDefault();
      setMeasurementKind(visibleTabs[nextIndex].dataset.measurementTab, true);
    };
    measurementTabs.forEach((tab) => {
      listen(tab, 'click', () => setMeasurementKind(tab.dataset.measurementTab, true), cleanups);
      listen(tab, 'keydown', onMeasurementTabKeydown, cleanups);
    });
    if (measurementTabs.length) {
      const initialMeasurementTab = measurementTabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || measurementTabs[0];
      setMeasurementKind(initialMeasurementTab.dataset.measurementTab);
    }
    const renderVariantDimensions = (option) => {
      const kind = root.dataset.productDimensionsKind;
      if (kind !== 'pants' && kind !== 'complete-set') return;
      const dimensions = [...root.querySelectorAll('[data-product-dimensions]')];
      if (!dimensions.length) return;
      const selection = root.querySelector('[data-product-measurements-selection]');
      if (selection && option?.dataset.option1) selection.textContent = `(${option.dataset.option1})`;
      const lines = (option?.dataset.dimensions || '').split('||').map((line) => line.trim()).filter((line) => {
        const normalized = line.toLowerCase();
        return normalized.includes('waist') || normalized.includes('hip') || normalized.includes('full length');
      });
      const escapeHtml = (value) => value.replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
      const rows = lines.map((line) => {
        const separator = line.indexOf(':');
        if (separator === -1) return `<tr><th scope="row" colspan="2">${escapeHtml(line)}</th></tr>`;
        const label = line.slice(0, separator).trim();
        const value = line.slice(separator + 1).trim();
        return `<tr><th scope="row">${escapeHtml(label)}</th><td>${escapeHtml(value)}</td></tr>`;
      }).join('');
      dimensions.forEach((element) => {
        if (kind === 'complete-set' && element.dataset.dimensionsKind !== 'pants') return;
        element.innerHTML = rows;
        const group = element.closest('[data-dimensions-group]');
        if (group) {
          group.dataset.hasDimensions = String(lines.length > 0);
          if (kind !== 'complete-set') group.hidden = lines.length === 0;
        }
      });
      const sheet = root.querySelector('[data-product-measurements]');
      if (sheet) {
        const groups = [...sheet.querySelectorAll('[data-dimensions-group]')];
        if (kind === 'complete-set') {
          const pantsGroup = groups.find((group) => group.dataset.dimensionsGroup === 'pants');
          const pantsTab = measurementTabs.find((tab) => tab.dataset.measurementTab === 'pants');
          const pantsAvailable = lines.length > 0;
          if (pantsGroup) pantsGroup.dataset.hasDimensions = String(pantsAvailable);
          if (pantsTab) pantsTab.hidden = !pantsAvailable;
          if (!pantsAvailable && pantsTab?.getAttribute('aria-selected') === 'true') {
            setMeasurementKind('kimono');
          }
        }
        sheet.hidden = groups.length > 0 ? !groups.some((group) => group.dataset.hasDimensions === 'true') : lines.length === 0;
      }
    };
    const applyVariant = (option, updateUrl = true) => {
      if (!option) return;
      const available = option.dataset.available === 'true';
      if (select && select.value !== option.value) select.value = option.value;
      const price = root.querySelector('[data-product-price]');
      const dispatch = root.querySelector('[data-preorder-terms]');
      if (price) {
        const currentPrice = price.querySelector('[data-product-current-price]');
        const comparePrice = price.querySelector('[data-product-compare-price]');
        if (currentPrice) currentPrice.textContent = option.dataset.price || '';
        if (comparePrice) {
          comparePrice.textContent = option.dataset.comparePrice || '';
          comparePrice.hidden = !option.dataset.comparePrice;
        }
      }
      renderVariantDimensions(option);
      if (dispatch) dispatch.hidden = !(available && form.dataset.productAcquisition === 'preorder');
      if (remakeOpener) remakeOpener.hidden = available;
      root.querySelectorAll('[data-option-index]').forEach((fieldset, index) => {
        const value = option.dataset[`option${index + 1}`];
        fieldset.querySelectorAll('[data-option-value]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.optionValue === value)));
      });
      if (submit) {
        const cartState = variantCartStates.get(String(option.value)) || { kind: 'available', canUpgrade: false };
        const isBlocked = cartState.kind === 'exact' || (cartState.kind === 'overlap' && !cartState.canUpgrade);
        submit.disabled = !available || isBlocked;
        const label = !available ? form.dataset.collectedLabel : cartState.canUpgrade ? form.dataset.completeSetLabel : cartState.kind !== 'available' ? form.dataset.alreadyLabel : form.dataset.productAcquisition === 'preorder' ? form.dataset.preorderLabel : form.dataset.addLabel;
        const labelElement = submit.querySelector('[data-add-to-bag-label]');
        if (labelElement) labelElement.textContent = label;
        else submit.textContent = label;
        if (overlapNote) {
          overlapNote.hidden = cartState.kind !== 'overlap';
          overlapNote.textContent = cartState.kind === 'overlap' ? cartState.canUpgrade ? form.dataset.upgradeLabel : form.dataset.overlapLabel : '';
        }
      }
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', url);
      }
    };
    const updateOptionAvailability = () => {
      root.querySelectorAll('[data-option-index]').forEach((fieldset, index) => fieldset.querySelectorAll('[data-option-value]').forEach((button) => {
        const values = selectedValues();
        values[index] = button.dataset.optionValue;
        button.disabled = !variants().some((option) => matchesVariant(option, values));
      }));
    };
    const onSelectChange = () => { applyVariant(select.selectedOptions[0]); updateOptionAvailability(); };
    const onOptionClick = (event) => {
      const button = event.currentTarget;
      const fieldset = button.closest('[data-option-index]');
      fieldset?.querySelectorAll('[data-option-value]').forEach((peer) => peer.setAttribute('aria-pressed', String(peer === button)));
      const option = selectedVariant();
      applyVariant(option);
      updateOptionAvailability();
    };
    const setProductStatus = (message, { focus = false, state = '' } = {}) => {
      setStatus(status, message, focus);
      if (state) status?.setAttribute('data-state', state);
      else status?.removeAttribute('data-state');
    };
    const setSubmitLoading = (loading) => {
      if (!submit) return;
      if (loading) {
        submit.setAttribute('data-loading', 'true');
        submit.setAttribute('aria-busy', 'true');
        submit.setAttribute('aria-label', form.dataset.loadingLabel);
      } else {
        submit.removeAttribute('data-loading');
        submit.removeAttribute('aria-busy');
        submit.removeAttribute('aria-label');
      }
    };
    const setSubmitLabel = (label) => {
      if (!submit) return;
      const labelElement = submit.querySelector('[data-add-to-bag-label]');
      if (labelElement) labelElement.textContent = label;
      else submit.textContent = label;
    };
    const onSubmit = async (event) => {
      event.preventDefault();
      if (submitting) return;
      const option = selectedVariant();
      if (!option || option.dataset.available !== 'true') { setProductStatus(form.dataset.unavailableLabel, { focus: true, state: 'error' }); return; }
      const knownState = variantCartStates.get(String(option.value)) || { kind: 'available', canUpgrade: false, conflicts: [] };
      if (knownState.kind !== 'available' && !knownState.canUpgrade) { setProductStatus(knownState.kind === 'overlap' ? form.dataset.overlapLabel : form.dataset.alreadyLabel, { focus: true, state: 'error' }); return; }
      submitting = true;
      form.setAttribute('aria-busy', 'true');
      if (submit) submit.disabled = true;
      setSubmitLoading(true);
      setProductStatus(form.dataset.loadingLabel, { state: 'loading' });
      let addSucceeded = false;
      let componentsRemoved = false;
      let needsBagReview = false;
      let removedItems = [];
      try {
        const cart = await getCart();
        const cartState = cartStateForVariant(cart, option);
        variantCartStates.set(String(option.value), cartState);
        if (cartState.kind !== 'available' && !cartState.canUpgrade) {
          setProductStatus(cartState.kind === 'overlap' ? form.dataset.overlapLabel : form.dataset.alreadyLabel, { focus: true, state: 'error' });
          applyVariant(option, false);
          return;
        }
        if (cartState.canUpgrade) {
          removedItems = cartState.conflicts.map((item) => ({ id: item.variant_id, quantity: item.quantity, properties: item.properties || {} }));
          const updates = Object.fromEntries(cartState.conflicts.map((item) => [item.key, 0]));
          let removeResponse;
          try {
            removeResponse = await fetch(`${storeRoot()}cart/update.js`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
          } catch (removeError) {
            needsBagReview = true;
            throw removeError;
          }
          if (!removeResponse.ok) {
            needsBagReview = true;
            throw new Error('Set upgrade could not remove components');
          }
          componentsRemoved = true;
        }
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        let response;
        try {
          const action = new URL(form.action, window.location.origin).href;
          const formData = new FormData(form);
          const properties = {};
          for (const [name, value] of formData) {
            const property = /^properties\[(.+)\]$/.exec(name);
            if (property && value) properties[property[1]] = value;
          }
          response = await fetch(action.endsWith('.js') ? action : `${action}.js`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [{ id: Number(option.value), quantity: 1, ...(Object.keys(properties).length ? { properties } : {}) }] }), signal: controller.signal });
        } finally {
          window.clearTimeout(timeout);
        }
        if (!response.ok) throw new Error('Add to bag failed');
        addSucceeded = true;
        variantCartStates.set(String(option.value), { kind: 'exact', conflicts: [], canUpgrade: false });
        applyVariant(option, false);
        let successMessage = form.dataset.successLabel;
        let drawerError = false;
        let drawerOpened = false;
        try {
          const nextCart = await getCart();
          successMessage = `${successMessage} ${nextCart.item_count}.`;
        } catch (error) {
          drawerError = true;
        }
        const bag = document.getElementById('BagDialog');
        if (bag?.showModal) {
          try {
            await refreshBagDialog(bag);
            if (!openDialog(bag, document.querySelector('[data-cart-link]') || submit)) throw new Error('Bag dialog could not open');
            drawerOpened = true;
            drawerError = false;
          } catch (error) {
            drawerError = true;
          }
        }
        if (drawerError) successMessage = `${successMessage} ${form.dataset.refreshErrorLabel}`;
        setProductStatus(successMessage, { focus: drawerError || !drawerOpened, state: drawerError ? 'error' : 'success' });
      } catch (error) {
        if (!addSucceeded && componentsRemoved && removedItems.length) {
          try {
            const recoveryResponse = await fetch(`${storeRoot()}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ items: removedItems }) });
            if (!recoveryResponse.ok) throw new Error('Set upgrade could not restore components');
          } catch (rollbackError) {
            needsBagReview = true;
          }
        }
        if (addSucceeded) {
          setProductStatus(`${form.dataset.successLabel} ${form.dataset.refreshErrorLabel}`, { focus: true, state: 'error' });
          applyVariant(option, false);
        } else {
          let recoveryDrawerOpened = false;
          if (needsBagReview) {
            const bag = document.getElementById('BagDialog');
            try {
              if (bag?.showModal) {
                await refreshBagDialog(bag);
                recoveryDrawerOpened = openDialog(bag, document.querySelector('[data-cart-link]') || submit);
              } else {
                await getCart();
              }
            } catch (recoveryError) {}
          }
          setProductStatus(needsBagReview ? form.dataset.recoveryErrorLabel || form.dataset.errorLabel : form.dataset.errorLabel, { focus: !recoveryDrawerOpened, state: 'error' });
          if (submit) {
            submit.disabled = false;
            setSubmitLabel(knownState.canUpgrade ? form.dataset.completeSetLabel : form.dataset.productAcquisition === 'preorder' ? form.dataset.preorderLabel : form.dataset.addLabel);
          }
        }
      } finally {
        form.removeAttribute('aria-busy');
        setSubmitLoading(false);
        submitting = false;
      }
    };
    form.dataset.productAcquisition = root.dataset.productAcquisition || 'ready';
    listen(select, 'change', onSelectChange, cleanups);
    root.querySelectorAll('[data-option-value]').forEach((button) => listen(button, 'click', onOptionClick, cleanups));
    listen(form, 'submit', onSubmit, cleanups);
    const initial = selectedVariant();
    applyVariant(initial, false);
    updateOptionAvailability();
    getCart().then((cart) => { variants().forEach((option) => variantCartStates.set(String(option.value), cartStateForVariant(cart, option))); applyVariant(selectedVariant(), false); }).catch(() => {});
    productBlockedStates.set(root, { variants, variantCartStates, applyVariant, selectedVariant });
    listen(window, 'pageshow', (event) => {
      if (!event.persisted) return;
      resyncProductBlockedStates();
    }, cleanups);
    initializedElements.add(root);
    cleanupRoots.set(root, cleanups);
  };

  const resyncProductBlockedStates = async () => {
    let cart;
    try { cart = await getCart(); } catch (error) { return; }
    productBlockedStates.forEach(({ variants, variantCartStates, applyVariant, selectedVariant }) => {
      variantCartStates.clear();
      variants().forEach((option) => variantCartStates.set(String(option.value), cartStateForVariant(cart, option)));
      applyVariant(selectedVariant(), false);
    });
  };

  const initializeCartForms = (scope) => {
    elements(scope, 'form[action*="/cart"]:not([data-product-form])').forEach((form) => {
      if (initializedElements.has(form)) return;
      const cleanups = [];
      const status = form.querySelector('[data-cart-status]');
      const cartOverlapLabel = status?.dataset.cartOverlapLabel || '';
      const clamp = () => form.querySelectorAll('[data-quantity-max]').forEach((input) => { const max = Number(input.dataset.quantityMax); if (Number.isFinite(max) && Number(input.value) > max) input.value = String(max); });
      const hasPhysicalOverlap = () => {
        const seen = new Set();
        return [...form.querySelectorAll('[data-cart-line]')].some((line) => {
          if (Number(line.dataset.quantity) > 1) return true;
          const identity = physicalPieceIdentity(line.dataset.sku, line.dataset.variantId);
          const overlaps = identity.keys.some((key) => seen.has(key));
          identity.keys.forEach((key) => seen.add(key));
          return overlaps;
        });
      };
      const syncConflictState = () => {
        const conflict = hasPhysicalOverlap();
        const checkout = form.querySelector('[name="checkout"]');
        if (checkout) checkout.disabled = conflict;
        if (conflict) setStatus(status, cartOverlapLabel);
        else if (status?.textContent === cartOverlapLabel) setStatus(status, '');
        return conflict;
      };
      const dialog = form.closest('#BagDialog');
      if (dialog) {
        let removing = false;
        form.querySelectorAll('.z-bag-line__remove').forEach((link) => {
          const onRemove = async (event) => {
            const href = link.getAttribute('href');
            if (!href) return;
            event.preventDefault();
            if (removing) return;
            removing = true;
            form.setAttribute('aria-busy', 'true');
            const checkout = form.querySelector('[name="checkout"]');
            if (checkout) checkout.disabled = true;
            const lines = [...form.querySelectorAll('[data-cart-line]')];
            const lineIndex = Math.max(0, lines.indexOf(link.closest('[data-cart-line]')));
            let removalSucceeded = false;
            try {
              const response = await fetch(href, { headers: { Accept: 'text/html' } });
              if (!response.ok) throw new Error('Bag item removal failed');
              removalSucceeded = true;
              const refreshed = await refreshBagDialog(dialog, { focus: false });
              if (!refreshed) throw new Error('Bag refresh failed');
              const nextLinks = [...dialog.querySelectorAll('.z-bag-line__remove')];
              const focusTarget = nextLinks[Math.min(lineIndex, nextLinks.length - 1)]
                || dialog.querySelector('[data-dialog-initial-focus]')
                || dialog.querySelector('button, a[href]');
              window.requestAnimationFrame(() => { if (dialog.open) focusTarget?.focus({ preventScroll: true }); });
            } catch (error) {
              window.location.href = removalSucceeded ? form.action : href;
            } finally {
              form.removeAttribute('aria-busy');
              removing = false;
            }
          };
          listen(link, 'click', onRemove, cleanups);
        });
      }
      const onSubmit = async (event) => {
        clamp();
        if (event.submitter?.name === 'checkout' && form.getAttribute('aria-busy') === 'true') {
          event.preventDefault();
          return;
        }
        if (event.submitter?.name === 'checkout' && syncConflictState()) {
          event.preventDefault();
          setStatus(status, cartOverlapLabel, true);
          return;
        }
        if (form.closest('#BagDialog') && event.submitter?.name === 'update') {
          event.preventDefault();
          form.setAttribute('aria-busy', 'true');
          setStatus(status, 'Refreshing');
          try {
            const response = await fetch(form.action, { method: 'POST', headers: { Accept: 'text/html' }, body: new FormData(form) });
            if (!response.ok) throw new Error('Cart update failed');
            await getCart();
            await refreshBagDialog(form.closest('#BagDialog'));
            resyncProductBlockedStates();
          } catch (error) {
            setStatus(status, 'The bag could not be refreshed.', true);
          } finally {
            form.removeAttribute('aria-busy');
          }
        }
      };
      listen(form, 'submit', onSubmit, cleanups);
      syncConflictState();
      initializedElements.add(form);
      cleanupRoots.set(form, cleanups);
    });
  };

  const navigationMenuTimers = new WeakMap();
  const navigationMenuFrames = new WeakMap();
  const navigationMenus = () => [...document.querySelectorAll('[data-nav-menu]')];
  const clearNavigationMenuTimer = (item) => {
    const timer = navigationMenuTimers.get(item);
    if (!timer) return;
    window.clearTimeout(timer);
    navigationMenuTimers.delete(item);
  };
  const clearNavigationMenuFrame = (item) => {
    const frame = navigationMenuFrames.get(item);
    if (!frame) return;
    window.cancelAnimationFrame(frame);
    navigationMenuFrames.delete(item);
  };
  const syncHeaderMenuState = (item) => {
    const header = item.closest('[data-header]');
    if (!header) return;
    const menus = [...header.querySelectorAll('[data-nav-menu]')].filter((menu) => menu.getClientRects().length > 0);
    const menuOpen = menus.some((menu) => {
      const trigger = menu.querySelector('[data-nav-menu-trigger]');
      return trigger?.getAttribute('aria-expanded') === 'true' || menu.dataset.navMenuState === 'closing';
    });
    if (menuOpen) header.dataset.headerMenuOpen = 'true';
    else delete header.dataset.headerMenuOpen;
  };
  const navigationMenuHasPointer = (item) => {
    const panel = item.querySelector('[data-nav-menu-panel]');
    return item.matches(':hover') || panel?.matches(':hover');
  };
  const scheduleNavigationMenuClose = (item) => {
    const closeDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220;
    clearNavigationMenuTimer(item);
    const finish = () => {
      navigationMenuTimers.delete(item);
      if (navigationMenuHasPointer(item) || item.contains(document.activeElement)) return;
      closeNavigationMenu(item);
    };
    if (closeDelay === 0) finish();
    else navigationMenuTimers.set(item, window.setTimeout(finish, closeDelay));
  };
  const closeNavigationMenu = (item, restoreFocus = false) => {
    const trigger = item.querySelector('[data-nav-menu-trigger]');
    const panel = item.querySelector('[data-nav-menu-panel]');
    if (!trigger || !panel) return;
    clearNavigationMenuTimer(item);
    clearNavigationMenuFrame(item);
    trigger.setAttribute('aria-expanded', 'false');
    delete item.dataset.navOpenSource;
    item.dataset.navMenuState = 'closing';
    syncHeaderMenuState(item);
    if (restoreFocus) trigger.focus();
    const finishClose = () => {
      if (trigger.getAttribute('aria-expanded') === 'true') return;
      panel.hidden = true;
      delete item.dataset.navMenuState;
      syncHeaderMenuState(item);
    };
    const closeDelay = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 320;
    if (closeDelay === 0) finishClose();
    else navigationMenuTimers.set(item, window.setTimeout(finishClose, closeDelay));
  };
  const openNavigationMenu = (item, source = 'click') => {
    navigationMenus().forEach((peer) => { if (peer !== item) closeNavigationMenu(peer); });
    clearNavigationMenuTimer(item);
    clearNavigationMenuFrame(item);
    const trigger = item.querySelector('[data-nav-menu-trigger]');
    const panel = item.querySelector('[data-nav-menu-panel]');
    if (!trigger || !panel) return;
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    item.dataset.navOpenSource = source;
    item.dataset.navMenuState = 'opening';
    syncHeaderMenuState(item);
    navigationMenuFrames.set(item, window.requestAnimationFrame(() => {
      if (trigger.getAttribute('aria-expanded') === 'true') item.dataset.navMenuState = 'open';
      navigationMenuFrames.delete(item);
    }));
  };

  const initializeNavigation = (scope) => {
    const menus = elements(scope, '[data-nav-menu]');
    menus.forEach((item) => {
      if (initializedElements.has(item)) return;
      const trigger = item.querySelector('[data-nav-menu-trigger]');
      const cleanups = [];
      listen(trigger, 'click', (event) => {
        if (trigger.matches('a')) event.preventDefault();
        if (trigger.getAttribute('aria-expanded') !== 'true') openNavigationMenu(item);
        else if (item.dataset.navOpenSource === 'hover' || item.dataset.navOpenSource === 'focus') {
          clearNavigationMenuTimer(item);
          clearNavigationMenuFrame(item);
          item.dataset.navOpenSource = 'click';
          item.dataset.navMenuState = 'open';
          syncHeaderMenuState(item);
        }
        else closeNavigationMenu(item);
      }, cleanups);
      listen(item, 'pointerenter', () => {
        if (window.matchMedia('(hover: hover)').matches && item.dataset.navOpenSource !== 'click') openNavigationMenu(item, 'hover');
      }, cleanups);
      listen(item, 'pointerleave', () => {
        if (window.matchMedia('(hover: hover)').matches && item.dataset.navOpenSource === 'hover') {
          scheduleNavigationMenuClose(item);
        }
      }, cleanups);
      listen(item.querySelector('[data-nav-menu-panel]'), 'pointerenter', () => clearNavigationMenuTimer(item), cleanups);
      listen(item.querySelector('[data-nav-menu-panel]'), 'pointerleave', () => {
        if (window.matchMedia('(hover: hover)').matches && item.dataset.navOpenSource === 'hover') {
          scheduleNavigationMenuClose(item);
        }
      }, cleanups);
      listen(item, 'focusin', () => { if (item.dataset.navOpenSource !== 'click') openNavigationMenu(item, 'focus'); }, cleanups);
      listen(item, 'focusout', (event) => {
        if (item.contains(event.relatedTarget) || navigationMenuHasPointer(item)) return;
        closeNavigationMenu(item);
      }, cleanups);
      cleanups.push(() => { clearNavigationMenuTimer(item); clearNavigationMenuFrame(item); delete item.dataset.navMenuState; delete item.dataset.navOpenSource; });
      initializedElements.add(item);
      cleanupRoots.set(item, cleanups);
    });
  };

  const initializeHeaderState = (scope) => {
    elements(scope, '[data-header]').forEach((header) => {
      if (initializedElements.has(header)) return;
      const cleanups = [];
      let surface = document.querySelector('main [data-header-surface="dark"]');
      if (!surface) {
        header.dataset.headerState = 'solid';
        initializedElements.add(header);
        cleanupRoots.set(header, cleanups);
        return;
      }

      document.documentElement.dataset.headerContext = 'dark';
      let frameId = 0;
      let forcedSolid = false;
      let cachedSurfaceTop = surface.getBoundingClientRect().top + window.scrollY;
      const solidThreshold = () => cachedSurfaceTop + 84;

      const syncHeader = () => {
        frameId = 0;
        if (!surface) {
          delete document.documentElement.dataset.headerContext;
          header.dataset.headerState = 'solid';
          return;
        }
        document.documentElement.dataset.headerContext = 'dark';
        const current = header.dataset.headerState;
        const desired = forcedSolid
          ? 'solid'
          : window.scrollY > solidThreshold() + 46
            ? 'solid'
            : window.scrollY < solidThreshold() - 24
              ? 'overlay'
              : current;
        header.dataset.headerState = desired || 'overlay';
      };
      const scheduleSync = () => {
        if (!frameId) frameId = window.requestAnimationFrame(syncHeader);
      };
      const forceSolid = () => { forcedSolid = true; header.dataset.headerState = 'solid'; };
      const releaseSolid = (immediate = false) => {
        forcedSolid = false;
        if (immediate) syncHeader();
        else scheduleSync();
      };
      const refreshGeometry = () => {
        surface = document.querySelector('main [data-header-surface="dark"]');
        if (surface) cachedSurfaceTop = surface.getBoundingClientRect().top + window.scrollY;
      };
      listen(window, 'scroll', scheduleSync, cleanups, { passive: true });
      listen(window, 'resize', refreshGeometry, cleanups, { passive: true });
      elements(scope, '[data-dialog-open], [data-mobile-menu-open], [data-header-search-open]').forEach((opener) => listen(opener, 'click', forceSolid, cleanups));
      elements(document, '[data-dialog], [data-mobile-menu]').forEach((dialog) => listen(dialog, 'close', releaseSolid, cleanups));
      listen(document, 'zaynab:interaction-close', () => releaseSolid(true), cleanups);
      cleanups.push(() => {
        if (frameId) window.cancelAnimationFrame(frameId);
        delete document.documentElement.dataset.headerContext;
        delete header.dataset.headerState;
        delete header.dataset.headerMenuOpen;
        header.style.removeProperty('--header-surface-alpha');
        header.style.removeProperty('--header-surface-blur');
        header.style.removeProperty('--header-shadow-alpha');
      });
      syncHeader();
      initializedElements.add(header);
      cleanupRoots.set(header, cleanups);
    });
  };

  const markActiveNavigation = (scope) => {
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    elements(scope, '.z-header__nav > a, .z-header__nav > .z-nav-menu > a, .z-header__bag').forEach((link) => {
      const url = new URL(link.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      const linkPath = url.pathname.replace(/\/+$/, '') || '/';
      const isCollectionRoute = currentPath === '/collections' || currentPath.startsWith('/collections/');
      const collectionChild = isCollectionRoute && currentPath !== '/collections/all' && link.dataset.navParent === 'collections';
      if (linkPath === currentPath || collectionChild) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
  };

  const initializeHeaderSearch = (scope) => {
    elements(scope, '[data-header-search]').forEach((panel) => {
      if (initializedElements.has(panel)) return;
      const trigger = document.querySelector('[data-header-search-open]');
      const input = panel.querySelector('[data-header-search-input]');
      const cleanups = [];
      let closeTimer;
      const setSearchScrollLock = (isLocked) => {
        if (isLocked) document.documentElement.dataset.headerSearchOpen = 'true';
        else delete document.documentElement.dataset.headerSearchOpen;
      };
      const setTriggerState = (isOpen) => {
        trigger?.setAttribute('aria-expanded', String(isOpen));
      };
      const open = () => {
        window.clearTimeout(closeTimer);
        document.querySelector('[data-mobile-menu][open]')?.close();
        document.querySelectorAll('[data-dialog][open]').forEach((dialog) => closeDialog(dialog, { immediate: true }));
        panel.hidden = false;
        panel.dataset.headerSearchState = 'opening';
        setTriggerState(true);
        setSearchScrollLock(true);
        setInteractionState('search', trigger);
        window.requestAnimationFrame(() => {
          panel.dataset.headerSearchState = 'open';
          input?.focus();
        });
      };
      closeHeaderSearch = (restoreFocus = false) => {
        if (panel.hidden) return;
        panel.dataset.headerSearchState = 'closing';
        setTriggerState(false);
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          panel.hidden = true;
          delete panel.dataset.headerSearchState;
          setSearchScrollLock(false);
        }, 260);
        clearInteractionState('search');
        if (restoreFocus) trigger?.focus();
      };
      listen(trigger, 'click', () => {
        if (panel.hidden) open();
        else closeHeaderSearch(true);
      }, cleanups);
      elements(panel, '[data-header-search-close]').forEach((closeButton) => listen(closeButton, 'click', () => closeHeaderSearch(true), cleanups));
      listen(panel, 'keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); closeHeaderSearch(true); return; }
        if (event.key !== 'Tab') return;
        const focusable = [...panel.querySelectorAll('input, button, a[href], [tabindex]:not([tabindex="-1"])')].filter((element) => !element.disabled && element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }, cleanups);
      cleanups.push(() => { window.clearTimeout(closeTimer); setSearchScrollLock(false); if (closeHeaderSearch) closeHeaderSearch = () => {}; });
      initializedElements.add(panel);
      cleanupRoots.set(panel, cleanups);
    });
  };

  const initializeMobileMenuV2 = (scope) => {
    elements(scope, '[data-mobile-menu]').forEach((menu) => {
      if (initializedElements.has(menu)) return;
      const opener = document.querySelector('[data-mobile-menu-open]');
      const closeButton = menu.querySelector('[data-mobile-menu-close]');
      const rootView = menu.querySelector('[data-mobile-menu-view="root"]');
      const views = [...menu.querySelectorAll('[data-mobile-menu-view]')];
      const parents = [...menu.querySelectorAll('[data-mobile-menu-parent]')];
      const backButtons = [...menu.querySelectorAll('[data-mobile-menu-back]')];
      if (!opener) return;
      const cleanups = [];
      const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
      let activeView = rootView;
      let activeParent = null;
      let viewTimer;
      let closeTimer;
      const resetView = (restoreFocus = false) => {
        window.clearTimeout(viewTimer);
        views.forEach((view) => {
          view.hidden = view !== rootView;
          view.classList.toggle('is-active', view === rootView);
          delete view.dataset.mobileMenuViewState;
        });
        parents.forEach((parent) => parent.setAttribute('aria-expanded', 'false'));
        activeView = rootView;
        activeParent = null;
        menu.dataset.mobileMenuLevel = 'root';
        if (restoreFocus) window.requestAnimationFrame(() => rootView?.focus());
      };
      const showView = (view, parent) => {
        if (!view || view === activeView) return;
        const previousView = activeView;
        window.clearTimeout(viewTimer);
        view.hidden = false;
        previousView?.setAttribute('data-mobile-menu-view-state', 'leaving');
        view.setAttribute('data-mobile-menu-view-state', 'entering');
        activeParent = parent;
        const duration = drawerMotionDuration();
        if (duration === 0) {
          previousView.hidden = true;
          previousView.classList.remove('is-active');
          view.classList.add('is-active');
          delete previousView.dataset.mobileMenuViewState;
          delete view.dataset.mobileMenuViewState;
          view.focus({ preventScroll: true });
        } else {
          window.requestAnimationFrame(() => { previousView?.classList.remove('is-active'); view.classList.add('is-active'); view.focus({ preventScroll: true }); });
          viewTimer = window.setTimeout(() => {
            previousView.hidden = true;
            delete previousView.dataset.mobileMenuViewState;
            delete view.dataset.mobileMenuViewState;
          }, duration);
        }
        parents.forEach((peer) => peer.setAttribute('aria-expanded', String(peer === parent)));
        activeView = view;
        menu.dataset.mobileMenuLevel = 'nested';
      };
      const goBack = () => {
        if (!activeView || activeView === rootView) return;
        const previousView = activeView;
        const previousParent = activeParent;
        window.clearTimeout(viewTimer);
        rootView.hidden = false;
        previousView.setAttribute('data-mobile-menu-view-state', 'leaving');
        rootView.setAttribute('data-mobile-menu-view-state', 'entering');
        const duration = drawerMotionDuration();
        if (duration === 0) {
          previousView.hidden = true;
          previousView.classList.remove('is-active');
          rootView.classList.add('is-active');
          delete previousView.dataset.mobileMenuViewState;
          delete rootView.dataset.mobileMenuViewState;
          previousParent?.focus({ preventScroll: true });
        } else {
          window.requestAnimationFrame(() => { previousView.classList.remove('is-active'); rootView.classList.add('is-active'); previousParent?.focus({ preventScroll: true }); });
          viewTimer = window.setTimeout(() => {
            previousView.hidden = true;
            delete previousView.dataset.mobileMenuViewState;
            delete rootView.dataset.mobileMenuViewState;
          }, duration);
        }
        parents.forEach((parent) => parent.setAttribute('aria-expanded', 'false'));
        activeView = rootView;
        activeParent = null;
        menu.dataset.mobileMenuLevel = 'root';
      };
      const setOpenerState = (isOpen) => { opener.setAttribute('aria-expanded', String(isOpen)); opener.setAttribute('aria-label', isOpen ? opener.dataset.mobileMenuCloseLabel : opener.dataset.mobileMenuLabel); };
      const close = (restoreFocus = true) => {
        if (!menu.open || menu.dataset.mobileMenuState === 'closing') return;
        window.clearTimeout(closeTimer);
        menu.dataset.mobileMenuState = 'closing';
        setOpenerState(false);
        clearInteractionState('menu');
        window.setTimeout(() => {
          if (!menu.open) return;
          resetView(false);
          menu.close();
          delete menu.dataset.mobileMenuState;
          if (dialogOpener === opener) dialogOpener = null;
          if (restoreFocus) window.requestAnimationFrame(() => opener.focus());
        }, drawerMotionDuration());
      };
      listen(opener, 'click', () => {
        if (menu.open) { close(); return; }
        window.clearTimeout(closeTimer);
        closeHeaderSearch();
        document.querySelectorAll('[data-dialog][open]').forEach((dialog) => closeDialog(dialog, { immediate: true }));
        dialogOpener = opener;
        resetView(false);
        if (!menu.showModal) return;
        menu.showModal();
        menu.dataset.mobileMenuState = 'opening';
        setOpenerState(true);
        setInteractionState('menu', opener);
        window.requestAnimationFrame(() => {
          menu.dataset.mobileMenuState = 'open';
          menu.querySelector('.z-mobile-menu__search input')?.focus();
        });
      }, cleanups);
      listen(closeButton, 'click', () => close(), cleanups);
      parents.forEach((parent) => listen(parent, 'click', () => showView(document.getElementById(parent.getAttribute('aria-controls')), parent), cleanups));
      backButtons.forEach((backButton) => listen(backButton, 'click', () => goBack(), cleanups));
      listen(menu, 'keydown', (event) => {
        if (event.key === 'Escape') { event.preventDefault(); close(); return; }
        if (event.key !== 'Tab') return;
        const focusable = [...menu.querySelectorAll(focusableSelector)].filter((element) => element.offsetParent !== null);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }, cleanups);
      listen(menu, 'cancel', (event) => { event.preventDefault(); close(); }, cleanups);
      listen(menu, 'close', () => {
        window.clearTimeout(closeTimer);
        resetView(false);
        delete menu.dataset.mobileMenuState;
        setOpenerState(false);
        if (dialogOpener === opener) { dialogOpener = null; window.requestAnimationFrame(() => opener.focus()); }
        clearInteractionState('menu');
      }, cleanups);
      cleanups.push(() => window.clearTimeout(closeTimer));
      initializedElements.add(menu);
      cleanupRoots.set(menu, cleanups);
    });
  };

  const initializeBehindReveal = (scope) => {
    elements(scope, '[data-product-behind]').forEach((root) => {
      if (initializedElements.has(root)) return;
      const toggle = root.querySelector('[data-behind-toggle]');
      if (!toggle) return;
      const cleanups = [];
      const setOpen = (open) => {
        toggle.setAttribute('aria-expanded', String(open));
        root.classList.toggle('is-open', open);
      };
      listen(toggle, 'click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'), cleanups);
      initializedElements.add(root);
      cleanupRoots.set(root, cleanups);
    });
  };

  const cleanupScope = (scope) => {
    [...cleanupRoots.entries()].forEach(([root, cleanups]) => {
      if (root === scope || scope.contains?.(root)) { cleanups.forEach((cleanup) => cleanup()); cleanupRoots.delete(root); initializedElements.delete(root); productBlockedStates.delete(root); }
    });
  };

  const refreshHeaderState = () => {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    cleanupScope(header);
    initializeHeaderState(header);
  };

  const initialize = (scope = document) => {
    initializeDialogs(scope);
    initializePredictiveSearch(scope);
    initializeCareLinks(scope);
    initializeWorldGateways(scope);
    initializeMotion(scope);
    initializeHeroMedia(scope);
    initializeManifestoReveal(scope);
    initializeSealReveal(scope);
    initializeCollectionFilters(scope);
    initializeCollectionVideo(scope);
    elements(scope, '[data-product-root]').forEach(initializeProduct);
    elements(scope, '[data-product-root]').forEach(initializeProductGallery);
    initializeBehindReveal(scope);
    initializeCartForms(scope);
    initializeNavigation(scope);
    markActiveNavigation(scope);
    initializeHeaderSearch(scope);
    initializeMobileMenuV2(scope);
    initializeHeaderState(scope);
    if (!globalInitialized) {
      globalInitialized = true;
      listen(document, 'click', (event) => navigationMenus().forEach((item) => { if (!item.contains(event.target)) closeNavigationMenu(item); }), globalCleanups);
      listen(document, 'keydown', (event) => {
        if (event.key !== 'Escape') return;
        const openItem = navigationMenus().find((item) => item.querySelector('[data-nav-menu-trigger]')?.getAttribute('aria-expanded') === 'true');
        if (openItem) { event.preventDefault(); closeNavigationMenu(openItem, true); }
      }, globalCleanups);
      listen(window, 'pageshow', (event) => {
        if (!event.persisted) return;
        const bag = document.getElementById('BagDialog');
        if (bag?.open) refreshBagDialog(bag).catch(() => {});
      }, globalCleanups);
      cleanupRoots.set(document, globalCleanups);
      if (window.location.hash) {
        const careTarget = document.getElementById(window.location.hash.slice(1));
        focusCareTarget(careTarget);
      }
    }
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    window.requestAnimationFrame(refreshHeaderState);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    cleanupScope(event.target);
    window.requestAnimationFrame(refreshHeaderState);
  });
  document.addEventListener('shopify:section:reorder', (event) => {
    initialize(event.target || document);
    window.requestAnimationFrame(refreshHeaderState);
  });
  document.addEventListener('shopify:section:select', (event) => event.target?.setAttribute('data-theme-editor-selected', 'true'));
  document.addEventListener('shopify:section:deselect', (event) => event.target?.removeAttribute('data-theme-editor-selected'));
  document.addEventListener('shopify:block:select', (event) => event.target?.setAttribute('data-theme-editor-selected', 'true'));
  document.addEventListener('shopify:block:deselect', (event) => event.target?.removeAttribute('data-theme-editor-selected'));
})();
